import { useApiFetchUserLocation } from '#/api/apiFetchUserLocation';
import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { signUp } from '#/api/userEnvironment/apiUserEnvironment';
import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import type { FormUsageContext } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import type { SignupFormValues } from '#/components/SignupForm/SignupForm';
import { SignupForm } from '#/components/SignupForm/SignupForm';
import { useUserEnvironmentActions } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { authLoginRoute } from '#/modules/auth/routing/auth.routes';
import { useOrgSavedToast } from '#/modules/listing/orgs/hooks/useOrgSavedToast';
import { HttpError } from '#/utils/HttpError';
import { trackSignUp } from '#/utils/analytics/track/trackSignUp';
import { configureTrackJs } from '#/utils/configureTrackJs';
import { deleteCookie } from '#/utils/cookies';
import { getThirdPartyLoginUrl } from '#/utils/getThirdPartyLoginUrl';
import {
  searchLocationToUserLocation,
  userLocationToSearchLocation,
} from '#/utils/location';
import { resetRecaptcha } from '#/utils/recaptcha';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  redirectTo?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  savedListing?: { id: string; type: string; name: string };
  subscribedSubsite?: string | null;
  invite?: ApiInvite | null;
  onSignup?: ((user: ApiUser) => void) | null;
  onLogin?: (user: ApiUser) => void | null;
  uncloseable?: boolean;
};

export function SignupFormContainer({
  analyticsTitle,
  usageContext,
  redirectTo,
  firstName,
  lastName,
  email,
  savedListing,
  subscribedSubsite,
  invite,
  onSignup,
  onLogin,
  uncloseable,
}: Props) {
  const { locale } = useI18n();
  const { setUserEnvironment } = useUserEnvironmentActions();
  const [userLocation] = useApiFetchUserLocation();
  const { showOrgSavedToast } = useOrgSavedToast();

  const initialValues = {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    email: invite?.email ?? email ?? '',
    password: '',
    location: userLocationToSearchLocation(userLocation),
    gRecaptchaResponse: undefined,
  };

  const showLoginModal = (forEmail: string) => {
    showModal('LOGIN', {
      redirectTo,
      savedListing,
      subscribedSubsite,
      email: forEmail,
      uncloseable,
      onLogin,
    });
  };

  const getLoginUrlAndState = (forEmail: string | undefined) => ({
    to: authLoginRoute.with({}),
    state: {
      redirectTo,
      email: forEmail,
      inviteId: invite?.id,
      savedListing,
      subscribedSubsite,
    },
  });

  const getSocialLoginUrl = (provider: 'facebook' | 'google' | 'apple') =>
    getThirdPartyLoginUrl({
      locale,
      provider,
      redirectTo,
      inviteId: invite?.id,
      savedListing,
      subscribedSubsite,
    });

  const onSubmit: FormSubmitFunction<SignupFormValues> = ({
    values,
    handleError,
  }) => {
    deleteCookie('userDismissedNotificationPreferencesDialog');

    const signupInfo = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: values.password,
      gRecaptchaResponse: values.gRecaptchaResponse,
      ...searchLocationToUserLocation(values.location),
      inviteId: invite?.id,
      redirectTo,
      listing: savedListing,
      subscribeSubsiteId: subscribedSubsite,
    };
    signUp(signupInfo)
      .then((userEnvironment) => {
        trackSignUp(userEnvironment.user, 'Email Password');
        configureTrackJs(userEnvironment.user.id);
        setUserEnvironment(userEnvironment);

        if (savedListing?.type === 'ORG') showOrgSavedToast();

        onSignup?.(userEnvironment.user);
        return;
      })
      .catch((error) => {
        // Check if this is a VM email verification error
        if (error instanceof HttpError && error.responseJson) {
          const vmVerifyEmailError = error.responseJson.errors?.find(
            (e) => e.type === 'vmVerifyEmail',
          );

          if (vmVerifyEmailError) {
            // Show VM email verification modal instead of form error
            showModal('VM_EMAIL_VERIFICATION', {
              email: values.email,
            });
            return;
          }
        }

        resetRecaptcha();
        handleError(error, {
          recaptchaKey: values.recaptchaKey + 1,
          gRecaptchaResponse: undefined,
        });
      });
  };

  return (
    <SignupForm
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      initialValues={initialValues}
      invite={invite}
      showLoginModal={showLoginModal}
      getLoginUrlAndState={getLoginUrlAndState}
      getThirdPartyLoginUrl={getSocialLoginUrl}
      onSubmit={onSubmit}
    />
  );
}
